/* import __COLOCATED_TEMPLATE__ from './posting-create-budget.hbs'; */
import { scheduleOnce } from '@ember/runloop';
import { A } from '@ember/array';
import { computed } from '@ember/object';
import { readOnly, equal, alias } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import {
  POSTING_CREATE_COVER_EXPENSES_TYPE,
  POSTING_CREATE_ESTIMATED_DAY_TYPES,
  POSTING_CREATE_ESTIMATED_WHOLE_DAY_COUNT,
  POSTING_CREATE_FREELANCER_LEVEL,
  POSTING_CREATE_RATE_TYPE_TO_TITLES_MAP
} from '../../constants/routes/posting-create';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import { ASSIGNMENT_COMMAND_CONSTANTS } from '../../services/commands/assignment-commands';

const CLASS_NAME = 'SH-c-posting-create-budget';

/** The Test Matches Statuses. */
const STATUSES = ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS;

/** Map of the Test Matches Statuses to feedback types. */
const FEEDBACK_TYPES_BY_STATUS = {
  [STATUSES.BLOCK]: 'error',
  [STATUSES.GOOD]: 'success',
  [STATUSES.NONE]: 'none',
  [STATUSES.WARN]: 'warn',
};

const formatNumber = (value) => Number(value).toLocaleString('en-US');

/**
 * @type {number} Minimum days
 */
const POSTING_CREATE_ESTIMATED_CUSTOM_DAYS_MIN = POSTING_CREATE_ESTIMATED_WHOLE_DAY_COUNT + 1;

/**
 * This component is the budget section for the posting create and edit route
 */
export default Component.extend({
  /**
   * @inheritDoc
   */
  tagName: '',
  CLASS_NAME,

  /**
   * Register services
   */
  errorTracker: service('error-tracker'),
  state: service('states/routes/posting-create'),
  stateManager: service('state-managers/routes/posting-create'),

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  /*********************************
   * OPTIONAL PARAMS
   **********************************/

  /*********************************
   * LIFECYCLE FUNCTIONS
   **********************************/

  /*********************************
   * PRIVATE
   **********************************/

  /**
   * @type {Boolean}    True when the form elements should be disabled
   */
  _areFormElementsDisabled: readOnly('state.areFormElementsDisabled'),

  /**
   * @type {String}     Constant values of the cover expenses enum for the template.
   */
  _constantsCoverExpensesNo: POSTING_CREATE_COVER_EXPENSES_TYPE.NO,
  _constantsCoverExpensesYes: POSTING_CREATE_COVER_EXPENSES_TYPE.YES,

  /**
   * @type {String}     Constant values of the estimated day type enum for the template.
   */
  _constantEstimatedDayCustomValue: POSTING_CREATE_ESTIMATED_DAY_TYPES.CUSTOM_NUMBER_OF_DAYS,

  /**
   * @type {String}     Constant values of the freelancer levels the user can select.
   */
  _constantsFreelancerLevelBasic: POSTING_CREATE_FREELANCER_LEVEL.BASIC,
  _constantsFreelancerLevelPremium: POSTING_CREATE_FREELANCER_LEVEL.PREMIUM,
  _constantsFreelancerLevelStandard: POSTING_CREATE_FREELANCER_LEVEL.STANDARD,

  /**
   * @type {Array<String>}     Constant values of the icons for the freelancer level block buttons.
   */
  _constantsFreelancerLevelBasicIcons: ['attach_money'],
  _constantsFreelancerLevelPremiumIcons: ['attach_money', 'attach_money', 'attach_money'],
  _constantsFreelancerLevelStandardIcons: ['attach_money', 'attach_money'],

  /**
   * The array of whole day object bags that we will show.
   *
   * [{
   *    title: '1',
   *    value: 'whole-day-1'
   *    selected: boolean
   *  },{...}]
   *
   * @type {Ember.MutableArray<Object>}
   */
  get _estimatedWholeDays() {
    let wholeDayBlockButtons = A(),
      wholeDayCountSupported = POSTING_CREATE_ESTIMATED_WHOLE_DAY_COUNT;

    for (let i = 1; i <= wholeDayCountSupported; i++) {
      let wholeDayValue = `${POSTING_CREATE_ESTIMATED_DAY_TYPES.WHOLE_DAY}${i}`;

      wholeDayBlockButtons.addObject({
        title: i,
        value: wholeDayValue,
      });
    }

    return wholeDayBlockButtons;
  },

  /**
   * Maps the estimated days enum to the selection
   */
  _estimatedWholeDayGuidIsSelectedMap: computed(
    '_estimatedWholeDays.@each.value',
    '_updatedEstimatedDayTypeEnum',
    function() {
      const selection = this._updatedEstimatedDayTypeEnum;
      return this._estimatedWholeDays.reduce((accum, bag) => {
        accum[bag.value] = bag.value === selection;
        return accum;
      }, {});
   }
  ),

  /**
   * @type {Boolean}    True when cover expenses yes or no enum has been selected.
   */
  _isCoverExpensesNoSelected: equal('_updatedCoverExpenses', POSTING_CREATE_COVER_EXPENSES_TYPE.NO),
  _isCoverExpensesYesSelected: equal('_updatedCoverExpenses', POSTING_CREATE_COVER_EXPENSES_TYPE.YES),

  /**
   * @type {Boolean}    True when custom days or half days are selected
   */
  _isEstimatedCustomDaysSelected: equal('_updatedEstimatedDayTypeEnum', POSTING_CREATE_ESTIMATED_DAY_TYPES.CUSTOM_NUMBER_OF_DAYS),

  /**
   * @type {number} Minimum days for custom input
   */
  _estimatedCustomDaysMin: POSTING_CREATE_ESTIMATED_CUSTOM_DAYS_MIN,

  /**
   * @type {Boolean}    Booleans for the block buttons for what rate type is selected.
   */
  _isFlatRateSelected: equal('_updatedRateType', ASSIGNMENT.RATE_TYPE.FLAT),
  _isDayRateSelected: equal('_updatedRateType', ASSIGNMENT.RATE_TYPE.DAY),

  /**
   * @type {Boolean}    True when the freelancer level is equal to the specific enum.
   */
  _isFreelancerLevelBasicSelected: equal('_updatedFreelancerLevel', POSTING_CREATE_FREELANCER_LEVEL.BASIC),
  _isFreelancerLevelPremiumSelected: equal('_updatedFreelancerLevel', POSTING_CREATE_FREELANCER_LEVEL.PREMIUM),
  _isFreelancerLevelStandardSelected: equal('_updatedFreelancerLevel', POSTING_CREATE_FREELANCER_LEVEL.STANDARD),

  _hasRateError: computed('state.errors', function() {
    const minRateError = Boolean(this.state.errors?.[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPENSATION_MIN]);
    const maxRateError = Boolean(this.state.errors?.[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPENSATION_MAX]);
    return minRateError || maxRateError;
  }),

  _hasRateTypeError: computed('state.errors', function() {
    return Boolean(this.state.errors?.[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.RATE_TYPE]);
  }),

  /** @type {String} The feedback type of the budget. */
  _budgetFeedbackType: computed('state.testMatchesBudgetStatus', function () {
    return FEEDBACK_TYPES_BY_STATUS[this.get('state.testMatchesBudgetStatus')];
  }),

  get _dayRateType() {
    return ASSIGNMENT.RATE_TYPE.DAY;
  },

  get _flatRateType() {
    return ASSIGNMENT.RATE_TYPE.FLAT;
  },

  /** @type {String} The feedback type of the rating. */
  _ratingFeedbackType: computed('state.testMatchesRatingStatus', function () {
    return FEEDBACK_TYPES_BY_STATUS[this.get('state.testMatchesRatingStatus')];
  }),

  get _postingDayRateOptionTitle() {
    return POSTING_CREATE_RATE_TYPE_TO_TITLES_MAP[ASSIGNMENT.RATE_TYPE.DAY];
  },

  get _postingFlatRateOptionTitle() {
    return POSTING_CREATE_RATE_TYPE_TO_TITLES_MAP[ASSIGNMENT.RATE_TYPE.FLAT];
  },

  /**
   * @type {String}   Text being sent back from the api for the budget and rating text feedback.
   */
  _testMatchesBudgetText: readOnly('state.testMatchesBudgetText'),
  _testMatchesRatingText: readOnly('state.testMatchesRatingText'),

  /**
   * Total compensation range for the assignment taking into account the rateType
   *
   * @type {String}
   */
  totalCompensationRange: computed(
    '_hasRateError',
    '_updatedRateType',
    '_updatedBudgetLowValue',
    '_updatedBudgetHighValue',
    '_updatedEstimatedDays',
    function() {
      const {
        _hasRateError,
        _updatedRateType,
        _updatedBudgetLowValue,
        _updatedBudgetHighValue,
        _updatedEstimatedDays
      } = this;

      if (
          _hasRateError
          || _updatedRateType === null
          || !_updatedBudgetLowValue
          || !_updatedBudgetHighValue
          || !_updatedEstimatedDays
        ) {
        return '';
      }

      const estimatedDays = _updatedRateType === ASSIGNMENT.RATE_TYPE.FLAT ? 1 : _updatedEstimatedDays;

      const totalCompensationMin = formatNumber(_updatedBudgetLowValue * estimatedDays);
      const totalCompensationMax = formatNumber(_updatedBudgetHighValue * estimatedDays);
      if (totalCompensationMin === totalCompensationMax) return `$${totalCompensationMin}`;
      return `$${totalCompensationMin} - $${totalCompensationMax}`;
    }
  ),

  /**
   * NOTE - THESE ARE ALIASED TO THE TOP LEVEL ROUTE STATE
   *
   * These are the min and max budgets
   *
   * @type {Number}
   */
  _updatedBudgetLowValue: alias('state.updatedBudgetLowValue'),
  _updatedBudgetHighValue: alias('state.updatedBudgetHighValue'),

  /**
   * @type {String}   of type POSTING_CREATE_COVER_EXPENSES_TYPE.*
   */
  _updatedCoverExpenses: readOnly('state.updatedCoverExpenses'),

  /**
   * NOTE - THIS IS ALIASED TO THE TOP LEVEL ROUTE STATE
   *
   * This is the custom value the user has entered for estimated number of days.
   *
   * @type {Number|null}
   */
  _updatedEstimatedCustomDays: alias('state.updatedEstimatedCustomDays'),

  /**
   * @type {Number}     Parsed number of days
   */
  _updatedEstimatedDays: readOnly('state.updatedEstimatedDays'),

  /**
   * @type {String}     The enum representing which estimated days button has been clicked. of type POSTING_CREATE_ESTIMATED_DAY_TYPES.*
   */
  _updatedEstimatedDayTypeEnum: readOnly('state.updatedEstimatedDaysTypeEnum'),

  /**
   * @type {String}     The enum for the level the user has selected. of type POSTING_CREATE_FREELANCER_LEVEL.*
   */
  _updatedFreelancerLevel: readOnly('state.updatedFreelancerLevel'),

  /**
   * NOTE - THESE ARE ALIASED TO THE TOP LEVEL ROUTE STATE
   *
   * This is the update rate type as selected by the user.
   *
   * @type {String}     of type Assignment.RATE_TYPE_*
   */
  _updatedRateType: alias('state.updatedRateType'),

  actions: {
    /**
     * Called when the user selects a rate type enum.
     *
     * @param {String} rateTypeSelected    of type Assignment.RATE_TYPE_*
     */
    onSelectRateTypeButtonClicked: function(rateTypeSelected) {
      this.set('state.errors', {
        ...this.state.errors,
        rate_type: undefined,
      });

      this.stateManager.updateRateType(rateTypeSelected);
    },

    /**
     * Called when the input of the custom estimated days changes.
     */
    onEstimatedCustomDaysInputChange({ target }) {
      // remove any decimal points.
      const estimatedCustomDays = target.value.replace(/[^0-9]/g, '');
      const customDaysInt = Number.parseInt(estimatedCustomDays);

      const updatedEstimatedCustomDays = (Number.isFinite(customDaysInt) && customDaysInt > 0)
        ? `${estimatedCustomDays}`
        : '';

      // typing in input will select the associated radio option
      this.setProperties({
        'state.updatedEstimatedCustomDays': `${updatedEstimatedCustomDays}`,
        'state.updatedEstimatedDaysTypeEnum': POSTING_CREATE_ESTIMATED_DAY_TYPES.CUSTOM_NUMBER_OF_DAYS,
      });
    },

    /**
     * Called when user selects a cover expenses button
     *
     * @param {String} coverExpensesType    of type POSTING_CREATE_COVER_EXPENSES_TYPE.*
     */
    onSelectCoverExpenses: function(coverExpensesType) {
      this.get('stateManager').updateCoverExpenses(coverExpensesType);
    },

    /**
     * Called when user selects a estimated day type button.
     *
     * @param {String} estimatedDayType     of type POSTING_CREATE_ESTIMATED_DAY_TYPES.*
     */
    onSelectEstimatedDaysClicked: function(estimatedDayType) {
      this.set('state.errors', {
        ...this.state.errors,
        estimated_days: undefined,
      });

      this.get('stateManager').updateEstimatedDays(estimatedDayType);
    },

    /**
     * Called when user selected a freelancer level
     *
     * @param freelancerLevel
     */
    onSelectFreelancerLevel: function(freelancerLevel) {
      this.get('stateManager').updateFreelancerLevel(freelancerLevel);
    },

    onInputBudgetLowValue(value) {
      this.set('state.errors', {
        ...this.state.errors,
        compensation_min: undefined,
        compensation_max: undefined,
      });

      this.stateManager.updatedBudgetLowValue(value);
    },

    onInputBudgetHighValue(value) {
      this.set('state.errors', {
        ...this.state.errors,
        compensation_min: undefined,
        compensation_max: undefined,
      });

      this.stateManager.updatedBudgetHighValue(value);
    },
  }
});
